import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import { DartsAdcCreateGamePayload, DartsAdcGames, DartsAdcGamesData, DartsAdcGamesResp, DartsGamesInitData, IAdcGamesFilterValues } from "./types";
import { api } from "../../../../services/api/api.service";
import { API_DARTS_ADC_GAMES, API_DARTS_GAMES_INIT_DATA, } from "../../../../services/api/api.urls";
import {toastr} from "react-redux-toastr";
import { IDartsGamesFilter } from "../../../../pages/Darts/Games/DartsGamesCRUD.interface";
import { COMPANY_ABBR } from "../../../../common/constants/company";
import { TryCatch } from "../../../../services/TryCatch";

const initialState: DartsAdcGames = {
    games: [] as DartsAdcGamesData[],
    scheduleSelectedGame: 0,
    selectedGame: null,
    initData: {} as DartsGamesInitData,
    isLoading: false,
    editMode: false,
    noContent: true,
    filterValues: {
        competition: 0,
        location: '',
        player: 0,
        from: '',
        status: 0,
        sort: 0,
    },
    totalPages:0,
    pageNum: 1,
    pageSize: 10,
}

export const getAdcDartsGamesInit = createAsyncThunk<DartsGamesInitData, void, { rejectValue: string }>(
    'cmsAdcDartsGamesInit/get',
    async(_, { rejectWithValue }) => {
        const result = await TryCatch(
            () => api.get(API_DARTS_GAMES_INIT_DATA).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, result.error);
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)
export const getAdcDartsGames = createAsyncThunk<DartsAdcGamesResp, string, { rejectValue: string }>(
    'cmsAdcDartsGames/get',
    async (queryString, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_DARTS_ADC_GAMES}?${queryString}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch standalone games.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "User registered");
        return result.data;
    }
)

export const createDartsAdcGame = createAsyncThunk<DartsAdcGamesData, DartsAdcCreateGamePayload, { rejectValue: string }>(
    'cmsAdcDartsGames/post',
    async (payload, {rejectWithValue}) => {
        let status = 0;
        const result = await TryCatch(
            () => api.post(API_DARTS_ADC_GAMES, payload).then(res => { status = res.status; return res.data; })
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, 'Failed to create a Standalone game.')
            return rejectWithValue(result.error);
        }
        if (status === 200 || status === 204) {
            toastr.warning(COMPANY_ABBR, "This game is already exist");
            return rejectWithValue('This game is already exists');

        }
        // if (status === 201)
        toastr.success(COMPANY_ABBR, "Standalone game has been created");
        return result.data.data;

    }
)

export const editDartsAdcGame = createAsyncThunk<DartsAdcGamesData, DartsAdcCreateGamePayload, { rejectValue: string }>(
    'cmsAdcDartsGames/edit',
    async (payload, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.put(`${API_DARTS_ADC_GAMES}`, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to edit a standalone game.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Standalone game has been edited");
        return result.data;
    }
)

export const deleteDartsAdcGame = createAsyncThunk<DartsAdcGamesData, number, { rejectValue: string }>(
    'cmsAdcDartsGames/delete',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.delete(`${API_DARTS_ADC_GAMES}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to delete a standalone game.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Standalone game has been deleted");
        return result.data;
    }
)

export const getOneDartsAdcGame = createAsyncThunk<DartsAdcGamesData, number, { rejectValue: string }>(
    'cmsAdcDartsGames/getSingle',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_DARTS_ADC_GAMES}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch a standalone game.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)

export const cmsAdcDartsGamesSlice = createSlice({
    name: 'cmsAdcDartsGames',
    reducers: {
        setSelectedAdcDartsGame: (state, action: PayloadAction<DartsAdcGamesData | null>) => {
            state.selectedGame = action.payload
        },
        clearAdcFilter: (state, action: PayloadAction<IAdcGamesFilterValues>) => {
            state.filterValues = action.payload
        },
        setAdcGameFilter: (state, action: PayloadAction<IDartsGamesFilter>) => {
            const {field, value} = action.payload;
            state.filterValues = {...state.filterValues, [field]: value};
        },
        setActivePage:(state,action) => {
            state.pageNum = action.payload
        },
        changePageSize:(state,action) => {
            state.pageSize = action.payload
        }
    },
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(getAdcDartsGamesInit.fulfilled, (state, action) => {
                state.initData = action.payload;
            })
            .addCase(getAdcDartsGames.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getAdcDartsGames.fulfilled, (state, action) => {
                state.games = action.payload.items
                if (action.payload.items.length) {
                    state.noContent = false;
                }
                state.pageNum = action.payload.pageNum
                state.totalPages = action.payload.totalPages
                state.isLoading = false;
            })
            .addCase(getAdcDartsGames.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(createDartsAdcGame.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createDartsAdcGame.fulfilled, (state, action) => {
                state.isLoading = false;
                state.games = state.games.concat(action.payload);
            })
            .addCase(createDartsAdcGame.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(editDartsAdcGame.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(editDartsAdcGame.fulfilled, (state, action) => {
                state.isLoading = false;
                state.games = state.games.map((game) => {
                    if (game.id === action.payload.id) {
                        game = action.payload
                    }
                    return game
                })

            })
            .addCase(editDartsAdcGame.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteDartsAdcGame.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteDartsAdcGame.fulfilled, (state, action) => {
                state.games = state.games.filter(
                    (game) => game.id !== action.meta.arg
                );
                state.isLoading = false;
            })
            .addCase(deleteDartsAdcGame.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(getOneDartsAdcGame.fulfilled, (state, action) => {
                state.selectedGame = action.payload;
            })
    },
});

export const {
    setSelectedAdcDartsGame,
    setAdcGameFilter,
    clearAdcFilter,
    setActivePage,
    changePageSize
} = cmsAdcDartsGamesSlice.actions
export default cmsAdcDartsGamesSlice.reducer;

